import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";

const LIMITE = 70;

function formatCOP(value) {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency: "COP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(value) {
  if (value === null || value === undefined) return "—";
  return value.toFixed(2) + "%";
}

function formatPeriodo(periodo) {
  if (!periodo) return "";
  const [year, month] = periodo.split("-");
  const meses = ["","Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${meses[parseInt(month)]} ${year}`;
}

function SeccionDetalle({ seccion, B_total }) {
  const [expandido, setExpandido] = useState(false);
  const pctDelTotal = B_total > 0 ? ((seccion.total_compromiso / B_total) * 100).toFixed(1) : "—";

  return (
    <div style={{ borderBottom: "1px solid #e5e7eb" }}>
      <div onClick={() => setExpandido(!expandido)} style={{
        display: "grid", gridTemplateColumns: "2fr 2fr 1fr 40px",
        padding: "0.75rem", cursor: "pointer",
        background: expandido ? "#eff6ff" : "transparent", alignItems: "center",
      }}>
        <span style={{ fontWeight: 700, color: "#163b8c" }}>
          Sección <strong>{seccion.seccion}</strong>
          <span style={{ marginLeft: "0.5rem", fontSize: "0.75rem",
            background: "#dbeafe", color: "#1e40af",
            padding: "0.15rem 0.5rem", borderRadius: "999px" }}>
            {seccion.items.length} rubros
          </span>
        </span>
        <span style={{ textAlign: "right", fontWeight: 600 }}>{formatCOP(seccion.total_compromiso)}</span>
        <span style={{ textAlign: "right", color: "#6b7280" }}>{pctDelTotal}%</span>
        <span style={{ textAlign: "center" }}>{expandido ? "▲" : "▼"}</span>
      </div>

      {expandido && (
        <div style={{ background: "#f8faff", borderTop: "1px solid #dbeafe" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr",
            padding: "0.5rem 0.75rem", background: "#dbeafe",
            fontSize: "0.8rem", fontWeight: 700, color: "#1e40af" }}>
            <span>CCPET</span><span>NOMBRE</span>
            <span style={{ textAlign: "right" }}>COMPROMISO</span>
          </div>
          {seccion.items.map((item, idx) => (
            <div key={item.ccpet + idx} style={{
              display: "grid", gridTemplateColumns: "1fr 2fr 2fr",
              padding: "0.5rem 0.75rem", borderBottom: "1px solid #e0eaff",
              fontSize: "0.85rem", background: idx % 2 === 0 ? "#fff" : "#f0f6ff",
            }}>
              <span style={{ fontFamily: "monospace", color: "#374151" }}>{item.ccpet}</span>
              <span style={{ color: "#4b5563", paddingRight: "0.5rem" }}>{item.nombre}</span>
              <span style={{ textAlign: "right", fontWeight: 500 }}>{formatCOP(item.total_compromiso)}</span>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr",
            padding: "0.6rem 0.75rem", background: "#dbeafe",
            fontWeight: 700, fontSize: "0.85rem" }}>
            <span style={{ gridColumn: "1/3", color: "#1e40af" }}>Subtotal Sección {seccion.seccion}</span>
            <span style={{ textAlign: "right", color: "#1e40af" }}>{formatCOP(seccion.total_compromiso)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IndicadoresLey() {
  const navigate = useNavigate();
  const [indicadores, setIndicadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState(null);

  useEffect(() => {
    const fetchIndicadores = async () => {
      try {
        setLoading(true);
        const url = apiConfig.getUrl("/api/hacienda/indicadores-ley/");
        const response = await fetch(url, { credentials: "include" });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Error al obtener indicadores");
        if (data.indicadores?.length > 0) {
          setIndicadores(data.indicadores);
          setPeriodoSeleccionado(data.indicadores[data.indicadores.length - 1].periodo);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchIndicadores();
  }, []);

  const indicadorActual = indicadores.find((i) => i.periodo === periodoSeleccionado);

  const cardStyle = {
    background: "#fff", borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    padding: "1.5rem", marginBottom: "1.5rem",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "2rem 1.5rem" }}>
      <div style={{ maxWidth: "960px", margin: "0 auto" }}>
        <button type="button" onClick={() => navigate("/hacienda-presupuesto")}
          style={{ background: "#e5e7eb", color: "#111827", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem",
            cursor: "pointer", fontWeight: 600, marginBottom: "1.5rem" }}>
          ← Volver a Hacienda
        </button>

        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ margin: 0, color: "#163b8c", fontSize: "2rem", fontWeight: 800 }}>
            Indicadores de Ley 617
          </h1>
          <p style={{ marginTop: "0.5rem", color: "#6b7280" }}>
            Artículo 6 — Gastos de funcionamiento / Ingresos corrientes de libre destinación.
            Límite: <strong>70%</strong> (categoría segunda y tercera).
          </p>
        </div>

        {loading && <div style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>Cargando indicadores...</div>}

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "12px",
            padding: "1rem", color: "#991b1b", fontWeight: 600, marginBottom: "1rem" }}>
            ⚠️ {error}
          </div>
        )}

        {!loading && !error && indicadores.length === 0 && (
          <div style={cardStyle}>
            <p style={{ color: "#6b7280", margin: 0 }}>
              No hay cargues disponibles. Primero debes subir los archivos en el{" "}
              <span style={{ color: "#1d4ed8", cursor: "pointer", fontWeight: 600 }}
                onClick={() => navigate("/hacienda-presupuesto/transformador")}>
                Transformador Presupuestal
              </span>.
            </p>
          </div>
        )}

        {!loading && !error && indicadores.length > 0 && (
          <>
            {/* Selector periodo */}
            <div style={cardStyle}>
              <label style={{ fontWeight: 600, color: "#374151", marginBottom: "0.5rem", display: "block" }}>
                Seleccionar periodo:
              </label>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                {indicadores.map((ind) => (
                  <button key={ind.periodo} type="button"
                    onClick={() => setPeriodoSeleccionado(ind.periodo)}
                    style={{ padding: "0.5rem 1rem", borderRadius: "999px", border: "2px solid",
                      borderColor: periodoSeleccionado === ind.periodo ? "#1d4ed8" : "#e5e7eb",
                      background: periodoSeleccionado === ind.periodo ? "#1d4ed8" : "#fff",
                      color: periodoSeleccionado === ind.periodo ? "#fff" : "#374151",
                      fontWeight: 600, cursor: "pointer", fontSize: "0.9rem" }}>
                    {formatPeriodo(ind.periodo)}{ind.alerta && " ⚠️"}
                  </button>
                ))}
              </div>
            </div>

            {indicadorActual && (
              <>
                {/* Banner */}
                {indicadorActual.alerta ? (
                  <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "12px",
                    padding: "1rem 1.25rem", color: "#991b1b", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
                    ⚠️ ALERTA: El indicador de {formatPeriodo(indicadorActual.periodo)} es{" "}
                    <strong>{formatPct(indicadorActual.indicador_pct)}</strong>, superando el límite
                    legal del <strong>{indicadorActual.limite_pct}%</strong>.
                  </div>
                ) : (
                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "12px",
                    padding: "1rem 1.25rem", color: "#166534", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
                    ✅ El indicador de {formatPeriodo(indicadorActual.periodo)} es{" "}
                    <strong>{formatPct(indicadorActual.indicador_pct)}</strong>, dentro del límite
                    legal del <strong>{indicadorActual.limite_pct}%</strong>.
                  </div>
                )}

                {/* Barra + tabla resumen */}
                <div style={cardStyle}>
                  <h2 style={{ margin: "0 0 1rem", color: "#163b8c", fontSize: "1.1rem" }}>
                    Ley 617 — Administración Central
                  </h2>
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                      marginBottom: "0.4rem", fontSize: "0.9rem", color: "#374151" }}>
                      <span>Ejecución: <strong>{formatPct(indicadorActual.indicador_pct)}</strong></span>
                      <span>Límite: <strong>{indicadorActual.limite_pct}%</strong></span>
                    </div>
                    <div style={{ background: "#e5e7eb", borderRadius: "999px",
                      height: "20px", position: "relative", overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(indicadorActual.indicador_pct ?? 0, 100)}%`,
                        height: "100%", background: indicadorActual.alerta ? "#ef4444" : "#22c55e",
                        borderRadius: "999px", transition: "width 0.5s ease" }} />
                      <div style={{ position: "absolute", top: 0, left: `${LIMITE}%`,
                        width: "2px", height: "100%", background: "#1d4ed8" }} />
                    </div>
                    <div style={{ marginTop: "0.25rem", fontSize: "0.8rem", color: "#6b7280",
                      paddingLeft: `${LIMITE - 2}%` }}>↑ 70%</div>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                    <tbody>
                      <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                          <strong>A</strong> — Recaudo fuente 11001
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                          {formatCOP(indicadorActual.A_recaudo)}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                          <strong>B</strong> — Compromisos fuente 11001 (Secciones 16 + 21)
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                          {formatCOP(indicadorActual.B_compromisos)}
                        </td>
                      </tr>
                      <tr style={{ background: indicadorActual.alerta ? "#fef2f2" : "#f0fdf4" }}>
                        <td style={{ padding: "0.75rem 0.5rem", fontWeight: 700, color: "#163b8c" }}>
                          Indicador (B / A)
                        </td>
                        <td style={{ padding: "0.75rem 0.5rem", textAlign: "right",
                          fontWeight: 800, fontSize: "1.1rem",
                          color: indicadorActual.alerta ? "#dc2626" : "#16a34a" }}>
                          {formatPct(indicadorActual.indicador_pct)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Detalle secciones con acordeón */}
                {indicadorActual.detalle_secciones?.length > 0 && (
                  <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
                    <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid #e5e7eb" }}>
                      <h2 style={{ margin: 0, color: "#163b8c", fontSize: "1.1rem" }}>
                        Detalle por Sección Presupuestal
                      </h2>
                      <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem", color: "#6b7280" }}>
                        Haz clic en cada sección para ver el detalle por CCPET
                      </p>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 40px",
                      padding: "0.6rem 0.75rem", background: "#f0f4ff",
                      fontWeight: 700, fontSize: "0.85rem", color: "#374151" }}>
                      <span>SECCIÓN</span>
                      <span style={{ textAlign: "right" }}>COMPROMISOS</span>
                      <span style={{ textAlign: "right" }}>% DEL TOTAL B</span>
                      <span />
                    </div>
                    {indicadorActual.detalle_secciones.map((sec) => (
                      <SeccionDetalle key={sec.seccion} seccion={sec}
                        B_total={indicadorActual.B_compromisos} />
                    ))}
                    <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr 1fr 40px",
                      padding: "0.75rem", background: "#e5e7eb", fontWeight: 700 }}>
                      <span>Total B</span>
                      <span style={{ textAlign: "right" }}>{formatCOP(indicadorActual.B_compromisos)}</span>
                      <span style={{ textAlign: "right" }}>100%</span>
                      <span />
                    </div>
                  </div>
                )}

                {/* Evolución histórica */}
                {indicadores.length > 1 && (
                  <div style={cardStyle}>
                    <h2 style={{ margin: "0 0 1rem", color: "#163b8c", fontSize: "1.1rem" }}>
                      Evolución histórica
                    </h2>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                      <thead>
                        <tr style={{ background: "#f0f4ff" }}>
                          <th style={{ padding: "0.6rem 0.75rem", textAlign: "left" }}>Periodo</th>
                          <th style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>Recaudo (A)</th>
                          <th style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>Compromisos (B)</th>
                          <th style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>Indicador</th>
                          <th style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indicadores.map((ind) => (
                          <tr key={ind.periodo} onClick={() => setPeriodoSeleccionado(ind.periodo)}
                            style={{ borderBottom: "1px solid #e5e7eb", cursor: "pointer",
                              background: ind.periodo === periodoSeleccionado ? "#eff6ff" : "transparent" }}>
                            <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600 }}>
                              {formatPeriodo(ind.periodo)}
                            </td>
                            <td style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>
                              {formatCOP(ind.A_recaudo)}
                            </td>
                            <td style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>
                              {formatCOP(ind.B_compromisos)}
                            </td>
                            <td style={{ padding: "0.6rem 0.75rem", textAlign: "right",
                              fontWeight: 700, color: ind.alerta ? "#dc2626" : "#16a34a" }}>
                              {formatPct(ind.indicador_pct)}
                            </td>
                            <td style={{ padding: "0.6rem 0.75rem", textAlign: "center" }}>
                              {ind.alerta ? "⚠️ Alerta" : "✅ OK"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}